import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { Modal } from "../ui/modal";
import { Button } from "../ui/button";
import { Field, Input, Select, Textarea } from "../ui/input";
import { useToastStore } from "../../stores/toast";
import {
  CRM_PARTNER_LABELS,
  CRM_SECTOR_LABELS,
  CRM_SOURCE_LABELS,
  CRM_STATUS_LABELS,
  CRM_STATUS_ORDER,
  centsToInput,
  dateTimeLocalToIso,
  parseAmountToCents,
  toDateTimeLocalValue,
} from "../../lib/crm";
import type {
  CrmAccount,
  CrmAccountStatus,
  CrmPartner,
  CrmSector,
  CrmSource,
  Timestamp,
} from "@shared/types";

// Le bouton d'envoi vit dans le pied du panneau, hors du <form> : il s'y rattache
// par l'attribut form=, seule façon de garder la soumission native.
const FORM_ID = "crm-account-form";

interface AdminUser {
  id: string;
  name: string;
  role: "admin" | "user";
}

// Les deux sections repliables : leur contenu sert à compter les champs déjà
// renseignés et à décider si on les ouvre à l'arrivée.
const ADDRESS_FIELDS: Array<keyof FormState> = ["addressLine", "city", "province", "postalCode"];
const CONTRACT_FIELDS: Array<keyof FormState> = [
  "setupFee",
  "mrr",
  "commissionPct",
  "contractStartAt",
  "contractEndAt",
];

interface AccountFormModalProps {
  open: boolean;
  account?: CrmAccount;
  onClose: () => void;
  onSaved: (account: CrmAccount) => void;
}

interface FormState {
  name: string;
  legalName: string;
  sector: CrmSector;
  source: CrmSource;
  partner: CrmPartner;
  status: CrmAccountStatus;
  ownerUserId: string;
  website: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  addressLine: string;
  city: string;
  province: string;
  postalCode: string;
  setupFee: string;
  mrr: string;
  commissionPct: string;
  contractStartAt: string;
  contractEndAt: string;
  notes: string;
}

export function AccountFormModal({ open, account, onClose, onSaved }: AccountFormModalProps) {
  const push = useToastStore((s) => s.push);
  const [form, setForm] = useState<FormState>(() => toFormState(undefined));
  const [owners, setOwners] = useState<AdminUser[]>([]);
  const [saving, setSaving] = useState(false);

  // La fenêtre reste montée entre deux ouvertures : on repart du dossier courant
  // à chaque ouverture plutôt que de conserver une saisie abandonnée.
  useEffect(() => {
    if (open) setForm(toFormState(account));
  }, [open, account]);

  useEffect(() => {
    if (!open) return;
    api<AdminUser[]>("/api/users")
      .then((list) => setOwners(list.filter((u) => u.role === "admin")))
      .catch(() => setOwners([]));
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Le compteur du résumé suit la saisie en cours…
  const addressFilled = countFilled(form, ADDRESS_FIELDS);
  const contractFilled = countFilled(form, CONTRACT_FIELDS);

  // …tandis que l'ouverture par défaut se décide sur le dossier chargé : une
  // section ne doit pas se replier sous les doigts pendant qu'on tape. Une
  // section fermée qui contient déjà des données cacherait de l'existant, ce
  // qui serait pire que le mur de champs — d'où l'ouverture automatique.
  const openByDefault = useMemo(() => {
    const initial = toFormState(account);
    return {
      address: countFilled(initial, ADDRESS_FIELDS) > 0,
      contract: countFilled(initial, CONTRACT_FIELDS) > 0,
    };
  }, [account]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name || saving) return;

    const setupFeeCents = toCents(form.setupFee);
    const mrrCents = toCents(form.mrr);
    if (setupFeeCents === null || mrrCents === null) {
      push("danger", "Montant invalide", "Saisis un montant positif, par exemple 1 200,00.");
      return;
    }
    const commissionPct = toPct(form.commissionPct);
    if (commissionPct === null) {
      push("danger", "Commission invalide", "Le pourcentage doit être compris entre 0 et 100.");
      return;
    }

    const payload = {
      name,
      legalName: trimOrNull(form.legalName),
      sector: form.sector,
      source: form.source,
      partner: form.partner,
      status: form.status,
      ownerUserId: form.ownerUserId || null,
      website: trimOrNull(form.website),
      contactName: trimOrNull(form.contactName),
      contactRole: trimOrNull(form.contactRole),
      contactEmail: trimOrNull(form.contactEmail),
      contactPhone: trimOrNull(form.contactPhone),
      addressLine: trimOrNull(form.addressLine),
      city: trimOrNull(form.city),
      province: trimOrNull(form.province),
      postalCode: trimOrNull(form.postalCode),
      setupFeeCents,
      mrrCents,
      commissionPct,
      contractStartAt: dateInputToIso(form.contractStartAt),
      contractEndAt: dateInputToIso(form.contractEndAt),
      notes: trimOrNull(form.notes),
    };

    setSaving(true);
    try {
      const saved = account
        ? await api<CrmAccount>(`/api/crm/accounts/${account.id}`, {
            method: "PATCH",
            body: JSON.stringify(payload),
          })
        : await api<CrmAccount>("/api/crm/accounts", {
            method: "POST",
            body: JSON.stringify(payload),
          });
      push("success", account ? "Dossier mis à jour" : "Dossier créé", saved.name);
      onSaved(saved);
    } catch (err) {
      const forbidden = err instanceof ApiError && err.status === 403;
      push(
        "danger",
        account ? "Échec de la mise à jour" : "Échec de la création",
        forbidden ? "Le CRM est réservé aux administrateurs." : "Vérifie les champs saisis.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={account ? "Modifier le dossier" : "Nouveau dossier"}
      onClose={onClose}
      // Les boutons vivent dans le pied du panneau : le formulaire est long et
      // ne doit pas emporter sa validation hors de vue en défilant.
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="primary"
            disabled={saving || !form.name.trim()}
          >
            {saving ? "Enregistrement…" : account ? "Enregistrer" : "Créer le dossier"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="crm-form" onSubmit={submit}>
        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Identité</h3>
            <p className="form-section__hint">
              Comment le dossier s'appelle, d'où il vient et qui le suit.
            </p>
          </div>

          <div className="form-section__grid form-section__grid--full">
            <Field label="Nom du dossier">
              <Input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="ex : Bistro Laurier"
                autoFocus
              />
            </Field>
          </div>

          <div className="form-section__grid">
            <Field label="Raison sociale" hint="Nom légal si différent du nom commercial.">
              <Input value={form.legalName} onChange={(e) => set("legalName", e.target.value)} />
            </Field>
            <Field label="Secteur">
              <Select
                value={form.sector}
                onChange={(e) => set("sector", e.target.value as CrmSector)}
              >
                <LabelOptions labels={CRM_SECTOR_LABELS} />
              </Select>
            </Field>
            <Field label="Source">
              <Select
                value={form.source}
                onChange={(e) => set("source", e.target.value as CrmSource)}
              >
                <LabelOptions labels={CRM_SOURCE_LABELS} />
              </Select>
            </Field>
            <Field label="Partenaire">
              <Select
                value={form.partner}
                onChange={(e) => set("partner", e.target.value as CrmPartner)}
              >
                <LabelOptions labels={CRM_PARTNER_LABELS} />
              </Select>
            </Field>
            <Field label="Statut">
              <Select
                value={form.status}
                onChange={(e) => set("status", e.target.value as CrmAccountStatus)}
              >
                {CRM_STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {CRM_STATUS_LABELS[s]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Responsable">
              <Select value={form.ownerUserId} onChange={(e) => set("ownerUserId", e.target.value)}>
                <option value="">Non attribué</option>
                {owners.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Site web">
              <Input
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                placeholder="https://…"
              />
            </Field>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Contact principal</h3>
            <p className="form-section__hint">La personne à joindre en premier chez le client.</p>
          </div>

          <div className="form-section__grid">
            <Field label="Nom">
              <Input
                value={form.contactName}
                onChange={(e) => set("contactName", e.target.value)}
              />
            </Field>
            <Field label="Rôle">
              <Input
                value={form.contactRole}
                onChange={(e) => set("contactRole", e.target.value)}
                placeholder="ex : Propriétaire"
              />
            </Field>
            <Field label="Courriel">
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set("contactEmail", e.target.value)}
              />
            </Field>
            <Field label="Téléphone">
              <Input
                value={form.contactPhone}
                onChange={(e) => set("contactPhone", e.target.value)}
              />
            </Field>
          </div>
        </section>

        <details className="form-section" open={openByDefault.address}>
          <summary className="form-section__head">
            <Chevron />
            <span className="form-section__title">Adresse</span>
            <span className="form-section__count">— {filledLabel(addressFilled)}</span>
          </summary>

          <div className="form-section__grid form-section__grid--full">
            <Field label="Rue">
              <Input
                value={form.addressLine}
                onChange={(e) => set("addressLine", e.target.value)}
              />
            </Field>
          </div>

          <div className="form-section__grid">
            <Field label="Ville">
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
            </Field>
            <Field label="Province">
              <Input
                value={form.province}
                onChange={(e) => set("province", e.target.value)}
                placeholder="ex : QC"
              />
            </Field>
            <Field label="Code postal">
              <Input value={form.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
            </Field>
          </div>
        </details>

        <details className="form-section" open={openByDefault.contract}>
          <summary className="form-section__head">
            <Chevron />
            <span className="form-section__title">Contrat</span>
            <span className="form-section__count">— {filledLabel(contractFilled)}</span>
          </summary>

          <div className="form-section__grid">
            <Field label="Frais d'installation ($)">
              <Input
                value={form.setupFee}
                onChange={(e) => set("setupFee", e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
              />
            </Field>
            <Field label="Abonnement mensuel ($)" hint="MRR contractuel.">
              <Input
                value={form.mrr}
                onChange={(e) => set("mrr", e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
              />
            </Field>
            <Field label="Commission (%)" hint="Taux appliqué par défaut aux lignes de revenus.">
              <Input
                value={form.commissionPct}
                onChange={(e) => set("commissionPct", e.target.value)}
                placeholder="0"
                inputMode="decimal"
              />
            </Field>
            <Field label="Début de contrat">
              <Input
                type="date"
                value={form.contractStartAt}
                onChange={(e) => set("contractStartAt", e.target.value)}
              />
            </Field>
            <Field label="Fin de contrat">
              <Input
                type="date"
                value={form.contractEndAt}
                onChange={(e) => set("contractEndAt", e.target.value)}
              />
            </Field>
          </div>
        </details>

        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Notes</h3>
            <p className="form-section__hint">Tout ce qui n'entre dans aucun champ ci-dessus.</p>
          </div>

          <div className="form-section__grid form-section__grid--full">
            {/* Le titre de section tient lieu d'étiquette : un libellé « Notes »
                de plus juste en dessous ne dirait rien de neuf. */}
            <Textarea
              rows={4}
              aria-label="Notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Contexte, historique, points d'attention…"
            />
          </div>
        </section>
      </form>
    </Modal>
  );
}

// Chevron du dépliant : il pivote en CSS quand <details> passe à [open], donc
// aucun état à tenir en JavaScript.
function Chevron() {
  return (
    <svg
      className="form-section__chevron"
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function countFilled(state: FormState, keys: Array<keyof FormState>): number {
  return keys.filter((key) => state[key].trim() !== "").length;
}

/** « 2 renseignés ». En français, zéro reste au singulier. */
function filledLabel(count: number): string {
  return `${count} renseigné${count > 1 ? "s" : ""}`;
}

function LabelOptions<K extends string>({ labels }: { labels: Record<K, string> }) {
  return (
    <>
      {(Object.entries(labels) as Array<[K, string]>).map(([value, label]) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </>
  );
}

function toFormState(account: CrmAccount | undefined): FormState {
  return {
    name: account?.name ?? "",
    legalName: account?.legalName ?? "",
    sector: account?.sector ?? "custom",
    source: account?.source ?? "autre",
    partner: account?.partner ?? "umbeli",
    status: account?.status ?? "prospect",
    ownerUserId: account?.ownerUserId ?? "",
    website: account?.website ?? "",
    contactName: account?.contactName ?? "",
    contactRole: account?.contactRole ?? "",
    contactEmail: account?.contactEmail ?? "",
    contactPhone: account?.contactPhone ?? "",
    addressLine: account?.addressLine ?? "",
    city: account?.city ?? "",
    province: account?.province ?? "",
    postalCode: account?.postalCode ?? "",
    setupFee: centsToInput(account?.setupFeeCents),
    mrr: centsToInput(account?.mrrCents),
    commissionPct: account?.commissionPct ? String(account.commissionPct) : "",
    contractStartAt: isoToDateInput(account?.contractStartAt),
    contractEndAt: isoToDateInput(account?.contractEndAt),
    notes: account?.notes ?? "",
  };
}

function trimOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** Champ vide = 0 ; null signale une saisie inexploitable ou négative (refusée par l'API). */
function toCents(input: string): number | null {
  if (!input.trim()) return 0;
  const cents = parseAmountToCents(input);
  return cents === null || cents < 0 ? null : cents;
}

function toPct(input: string): number | null {
  if (!input.trim()) return 0;
  const value = Number(input.replace(",", "."));
  return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
}

// <input type="date"> ne transporte pas d'heure : on ancre à minuit local pour
// qu'une date de contrat ne recule pas d'un jour une fois sérialisée en UTC.
function dateInputToIso(value: string): string | null {
  return value ? dateTimeLocalToIso(`${value}T00:00`) : null;
}

function isoToDateInput(value: Timestamp | null | undefined): string {
  return toDateTimeLocalValue(value).slice(0, 10);
}
